if (!customElements.get('verrite-artist-process-carousel')) {
class VerriteArtistProcessCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-process-carousel-track]');
    this.slides = Array.from(this.querySelectorAll('[data-process-carousel-slide]'));
    this.realSlides = this.slides.filter((slide) => !slide.hasAttribute('data-process-carousel-clone'));
    this.previousButtons = Array.from(this.querySelectorAll('[data-process-carousel-previous]'));
    this.nextButtons = Array.from(this.querySelectorAll('[data-process-carousel-next]'));

    if (!this.track || this.realSlides.length === 0) return;

    this.activeIndex = 0;
    this.loopWidth = 0;
    this.frame = null;
    this.resumeAutoplayTimer = null;
    this.lastFrameTime = null;
    this.autoplayRemainder = 0;
    this.queuedFrame = null;
    this.isPointerDown = false;
    this.isDragging = false;
    this.startX = 0;
    this.startScrollLeft = 0;
    this.shouldResumeAutoplay = false;
    this.autoplayEnabled = this.dataset.autoplay !== 'false';
    this.autoplayInterval = Number(this.dataset.autoplayInterval) || 5000;

    this.handleScroll = () => this.queueUpdate();
    this.handleResize = () => {
      this.updateLoopMetrics();
      this.queueUpdate();
    };
    this.handleVisibilityChange = () => {
      this.lastFrameTime = null;
    };
    this.handleKeydown = (event) => this.handleKeyboard(event);
    this.handlePointerDown = (event) => this.startDrag(event);
    this.handlePointerMove = (event) => this.drag(event);
    this.handlePointerUp = () => this.endDrag();
    this.handlePointerCancel = () => this.endDrag();
    this.handlePointerLeave = () => {
      if (this.isPointerDown) this.endDrag();
    };
    this.handlePreviousClick = () => this.goTo(this.activeIndex - 1);
    this.handleNextClick = () => this.goTo(this.activeIndex + 1);

    this.track.addEventListener('scroll', this.handleScroll, { passive: true });
    this.track.addEventListener('keydown', this.handleKeydown);
    this.track.addEventListener('pointerdown', this.handlePointerDown);
    this.track.addEventListener('pointermove', this.handlePointerMove);
    this.track.addEventListener('pointerup', this.handlePointerUp);
    this.track.addEventListener('pointercancel', this.handlePointerCancel);
    this.track.addEventListener('pointerleave', this.handlePointerLeave);
    this.previousButtons.forEach((button) => button.addEventListener('click', this.handlePreviousClick));
    this.nextButtons.forEach((button) => button.addEventListener('click', this.handleNextClick));
    window.addEventListener('resize', this.handleResize);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    this.updateLoopMetrics();
    this.update();
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();

    this.track?.removeEventListener('scroll', this.handleScroll);
    this.track?.removeEventListener('keydown', this.handleKeydown);
    this.track?.removeEventListener('pointerdown', this.handlePointerDown);
    this.track?.removeEventListener('pointermove', this.handlePointerMove);
    this.track?.removeEventListener('pointerup', this.handlePointerUp);
    this.track?.removeEventListener('pointercancel', this.handlePointerCancel);
    this.track?.removeEventListener('pointerleave', this.handlePointerLeave);
    this.previousButtons?.forEach((button) => button.removeEventListener('click', this.handlePreviousClick));
    this.nextButtons?.forEach((button) => button.removeEventListener('click', this.handleNextClick));
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);

    if (this.queuedFrame) {
      cancelAnimationFrame(this.queuedFrame);
    }

    if (this.resumeAutoplayTimer) {
      clearTimeout(this.resumeAutoplayTimer);
    }
  }

  startAutoplay() {
    if (!this.autoplayEnabled || this.frame || this.realSlides.length < 2 || this.prefersReducedMotion()) return;

    this.track.toggleAttribute('data-autoplaying', true);
    this.lastFrameTime = null;
    this.autoplayRemainder = 0;
    this.frame = requestAnimationFrame((time) => this.tick(time));
  }

  stopAutoplay() {
    if (!this.frame) return;

    cancelAnimationFrame(this.frame);
    this.frame = null;
    this.lastFrameTime = null;
    this.track?.toggleAttribute('data-autoplaying', false);
  }

  tick(time) {
    if (document.visibilityState !== 'visible') {
      this.lastFrameTime = null;
      this.frame = requestAnimationFrame((nextTime) => this.tick(nextTime));
      return;
    }

    if (!this.loopWidth) {
      this.updateLoopMetrics();
    }

    if (!this.isPointerDown && this.loopWidth) {
      if (this.lastFrameTime !== null) {
        // Chrome may throttle requestAnimationFrame to one frame per second.
        // Keep the configured speed accurate without jumping after a hidden tab resumes.
        const seconds = Math.min((time - this.lastFrameTime) / 1000, 1);
        const pixelsPerSecond = this.loopWidth / ((this.autoplayInterval / 1000) * this.realSlides.length);
        this.autoplayRemainder += pixelsPerSecond * seconds;
        const pixelsToScroll = Math.floor(this.autoplayRemainder);

        if (pixelsToScroll > 0) {
          this.track.scrollLeft += pixelsToScroll;
          this.autoplayRemainder -= pixelsToScroll;
          this.normalizeScrollPosition();
        }
      }

      this.lastFrameTime = time;
    }

    this.frame = requestAnimationFrame((nextTime) => this.tick(nextTime));
  }

  startDrag(event) {
    if (event.button !== 0 && event.pointerType === 'mouse') return;

    const hasScheduledAutoplay = Boolean(this.resumeAutoplayTimer);

    if (this.resumeAutoplayTimer) {
      clearTimeout(this.resumeAutoplayTimer);
      this.resumeAutoplayTimer = null;
    }

    this.shouldResumeAutoplay = Boolean(this.frame) || hasScheduledAutoplay;
    this.stopAutoplay();
    this.isPointerDown = true;
    this.isDragging = false;
    this.startX = event.clientX;
    this.startScrollLeft = this.track.scrollLeft;
    this.track.setPointerCapture?.(event.pointerId);
  }

  drag(event) {
    if (!this.isPointerDown) return;

    const dragDistance = event.clientX - this.startX;

    if (Math.abs(dragDistance) > 3) {
      this.isDragging = true;
      this.track.toggleAttribute('data-dragging', true);
    }

    if (this.isDragging) {
      event.preventDefault();
      this.track.scrollLeft = this.startScrollLeft - dragDistance;
    }
  }

  endDrag() {
    if (!this.isPointerDown) return;

    this.isPointerDown = false;
    this.track.toggleAttribute('data-dragging', false);
    this.lastFrameTime = null;
    this.update();

    if (this.shouldResumeAutoplay) {
      this.resumeAutoplayTimer = setTimeout(() => {
        this.resumeAutoplayTimer = null;
        this.startAutoplay();
      }, 1200);
    }

    this.shouldResumeAutoplay = false;
  }

  handleKeyboard(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goTo(this.activeIndex - 1);
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goTo(this.activeIndex + 1);
    }
  }

  goTo(index) {
    const nextIndex = (index + this.realSlides.length) % this.realSlides.length;
    const nextSlide = this.realSlides[nextIndex];

    if (!nextSlide) return;

    const shouldResumeAutoplay = Boolean(this.frame);
    this.stopAutoplay();

    this.track.scrollTo({
      left: this.getSlideLeft(nextSlide),
      behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
    });

    this.setActiveIndex(nextIndex);

    if (shouldResumeAutoplay) {
      if (this.resumeAutoplayTimer) {
        clearTimeout(this.resumeAutoplayTimer);
      }

      this.resumeAutoplayTimer = setTimeout(() => {
        this.resumeAutoplayTimer = null;
        this.startAutoplay();
      }, 1200);
    }
  }

  queueUpdate() {
    if (this.queuedFrame) return;

    this.queuedFrame = requestAnimationFrame(() => {
      this.queuedFrame = null;
      this.update();
    });
  }

  update() {
    this.setActiveIndex(this.getClosestSlideIndex() % this.realSlides.length);
  }

  getClosestSlideIndex() {
    let closestIndex = 0;
    let closestDistance = Infinity;

    this.slides.forEach((slide, index) => {
      const distance = Math.abs(this.getSlideLeft(slide) - this.track.scrollLeft);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  updateLoopMetrics() {
    const firstClone = this.slides[this.realSlides.length];

    if (firstClone) {
      this.loopWidth = this.getSlideLeft(firstClone);
    } else {
      this.loopWidth = this.track.scrollWidth;
    }
  }

  normalizeScrollPosition() {
    if (!this.loopWidth) return;

    while (this.track.scrollLeft >= this.loopWidth) {
      this.track.scrollLeft -= this.loopWidth;
    }

    while (this.track.scrollLeft < 0) {
      this.track.scrollLeft += this.loopWidth;
    }
  }

  getSlideLeft(slide) {
    const slideRect = slide.getBoundingClientRect();
    const trackRect = this.track.getBoundingClientRect();

    return slideRect.left - trackRect.left + this.track.scrollLeft;
  }

  setActiveIndex(index) {
    this.activeIndex = index;

    this.slides.forEach((slide, slideIndex) => {
      slide.toggleAttribute('data-active', slideIndex % this.realSlides.length === index);
    });
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

  customElements.define('verrite-artist-process-carousel', VerriteArtistProcessCarousel);
}
